/* City Uniform — global behaviour. No dependencies. */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };

  /* ---------- Helpers ---------- */
  theme.fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.description || data.message || theme.strings.cartError);
      err.status = res.status;
      throw err;
    }
    return data;
  };

  theme.debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  /* Update every bag count in the header. */
  theme.updateBagCount = (count) => {
    document.querySelectorAll('[data-bag-count]').forEach((el) => {
      el.textContent = el.hasAttribute('aria-hidden') && count === 0 ? '' : String(count);
    });
    const label = document.querySelector('[data-bag-label]');
    if (label && theme.strings.bagItems) label.textContent = theme.strings.bagItems.replace('[count]', count);
  };

  theme.refreshBagCount = async () => {
    try {
      const cart = await theme.fetchJSON(`${theme.routes.cart}.js`);
      theme.updateBagCount(cart.item_count);
      return cart;
    } catch (e) { return null; }
  };

  /* Bag notice (live region) after add to bag. */
  const notice = document.getElementById('BagNotice');
  let noticeTimer;
  theme.showBagNotice = (text) => {
    if (!notice) return;
    notice.querySelector('[data-bag-notice-text]').textContent = text;
    notice.hidden = false;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { notice.hidden = true; }, 6000);
  };
  notice?.querySelector('[data-bag-notice-close]')?.addEventListener('click', () => { notice.hidden = true; });

  /* ---------- Dialogs (menu, search, filters, size guide) ---------- */
  let lastTrigger = null;
  const openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    lastTrigger = trigger || document.activeElement;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    trigger?.setAttribute('aria-expanded', 'true');
    const focusTarget = dialog.querySelector('[autofocus], input[type="search"]');
    if (focusTarget) focusTarget.focus();
  };
  const closeDialog = (dialog) => {
    if (!dialog?.open) return;
    dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.openDialog), opener);
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop closes the dialog.
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.querySelectorAll('dialog').forEach((dialog) => {
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      if (lastTrigger) {
        lastTrigger.setAttribute('aria-expanded', 'false');
        lastTrigger.focus();
        lastTrigger = null;
      }
    });
  });

  /* ---------- Desktop dropdowns: close on outside click / Escape ---------- */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-dropdown][open]').forEach((d) => {
      if (!d.contains(event.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('details[data-dropdown][open]');
    if (open) {
      open.removeAttribute('open');
      open.querySelector('summary')?.focus();
    }
  });
  // Only one dropdown open at a time.
  document.querySelectorAll('details[data-dropdown]').forEach((d) => {
    d.addEventListener('toggle', () => {
      if (!d.open) return;
      document.querySelectorAll('details[data-dropdown][open]').forEach((other) => { if (other !== d) other.removeAttribute('open'); });
    });
  });

  /* ---------- Auto-submit selects (sort, country, language) ---------- */
  document.addEventListener('change', (event) => {
    const el = event.target.closest('[data-auto-submit]');
    if (el && el.form) el.form.requestSubmit ? el.form.requestSubmit() : el.form.submit();
  });

  /* ---------- Facets: drop empty price inputs so URLs stay clean ---------- */
  document.querySelectorAll('[data-facets-form]').forEach((form) => {
    form.addEventListener('submit', () => {
      form.querySelectorAll('input[type="number"]').forEach((input) => { if (input.value === '') input.disabled = true; });
    });
  });

  /* ---------- Address forms: preselect saved country ---------- */
  document.querySelectorAll('select[data-default]').forEach((select) => {
    if (select.dataset.default) select.value = select.dataset.default;
  });
  document.querySelectorAll('[data-confirm]').forEach((btn) => {
    btn.addEventListener('click', (event) => {
      if (!window.confirm(btn.dataset.confirm)) event.preventDefault();
    });
  });

  /* ---------- Predictive search ---------- */
  const searchInput = document.querySelector('[data-predictive-search]');
  const resultsEl = document.querySelector('[data-predictive-results]');
  if (searchInput && resultsEl && theme.routes.predictiveSearch) {
    let controller;
    const run = theme.debounce(async () => {
      const q = searchInput.value.trim();
      if (q.length < 2) {
        resultsEl.innerHTML = '';
        searchInput.setAttribute('aria-expanded', 'false');
        return;
      }
      controller?.abort();
      controller = new AbortController();
      resultsEl.setAttribute('aria-busy', 'true');
      try {
        const params = new URLSearchParams({
          q,
          section_id: 'predictive-search',
          'resources[limit]': '4',
          'resources[type]': 'product,collection,page,article',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
        });
        const res = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#PredictiveSearchResults');
        resultsEl.innerHTML = section ? section.outerHTML : '';
        searchInput.setAttribute('aria-expanded', section ? 'true' : 'false');
      } catch (e) {
        if (e.name !== 'AbortError') resultsEl.innerHTML = '';
      } finally {
        resultsEl.removeAttribute('aria-busy');
      }
    }, 280);
    searchInput.addEventListener('input', run);
  }

  /* ---------- Product recommendations (lazy) ---------- */
  document.querySelectorAll('[data-recommendations]').forEach((el) => {
    const load = async () => {
      try {
        const res = await fetch(el.dataset.url);
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
      } catch (e) { /* recommendations are optional */ }
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { io.disconnect(); load(); }
      }, { rootMargin: '600px 0px' });
      io.observe(el);
    } else { load(); }
  });

  /* ---------- Header height variable (sticky toolbars) ---------- */
  const header = document.querySelector('.header-wrap');
  const setHeaderHeight = () => {
    if (!header) return;
    const sticky = header.classList.contains('header-wrap--sticky');
    document.documentElement.style.setProperty('--header-height', sticky ? `${header.offsetHeight}px` : '0px');
  };
  setHeaderHeight();
  window.addEventListener('resize', theme.debounce(setHeaderHeight, 150));

  /* Keep the bag count right after back/forward navigation (bfcache). */
  window.addEventListener('pageshow', (event) => { if (event.persisted) theme.refreshBagCount(); });

  window.theme = theme;
})();
